// Чистая логика страницы «Тарифы»: валидация ИНН, форматирование цен,
// выбор цены по периоду и запасная тарифная сетка (когда bit-billing недоступен).
// Канон сетки — спека docs/superpowers/specs/2026-07-22-paid-tariffs-saas-design.md.

// ИНН: строка ровно из 10 или 12 цифр (контрольные суммы не проверяем).
export function validateInn(inn) {
  return typeof inn === 'string' && /^(\d{10}|\d{12})$/.test(inn);
}

const rubFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

// 4500 → «4 500 ₽» (разряды через неразрывный пробел, без копеек).
export function formatRub(n) {
  return `${rubFormat.format(n)} ₽`;
}

// Сумма журнала операций: с копейками, как в выписке. formatRub округляет до
// рубля, а долг считается до копейки (billable_rub округляет вверх) — журнал
// человек читает именно чтобы нас проверить, и расхождение в нём недопустимо.
const rubExactFormat = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatRubExact(n) {
  return `${rubExactFormat.format(n)} ₽`;
}

// Цена плана за период: 'month' → price_month_rub, 'year' → price_year_rub, иначе null.
export function planPrice(plan, period) {
  if (!plan) return null;
  if (period === 'month') return plan.price_month_rub;
  if (period === 'year') return plan.price_year_rub;
  return null;
}

// Ставка минуты «Оплаты по факту» в заголовке карточки: 1 → «1,00 ₽».
const overageFormat = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numFormat = new Intl.NumberFormat('ru-RU');

// «Оплата по факту» — не бесплатный тариф, а поминутный без подписки: пакет
// минут в подарок каждый месяц, дальше рубль за минуту с пополняемого баланса.
// Рисовать его карточку ценой «Бесплатно» и строкой «сверх пакета» значит
// прятать ровно то, что мы продаём.
export const PAYG_PLAN_CODE = 'free';

// Названия тарифов для людей: код `small` в таблицах читается хуже, чем «Малый».
export const PLAN_TITLES = { free: 'Оплата по факту', small: 'Малый', medium: 'Средний', large: 'Большой' };

export function isPayg(plan) {
  return !!plan && plan.code === PAYG_PLAN_CODE;
}

// Крупная строка карточки: у подписок это цена периода, у «Оплаты по факту» —
// цена минуты. `unit` и `note` рисуются мелким рядом.
// Бэкенд отдаёт наивный UTC без суффикса `Z` (`2026-08-20T20:27:09.006000`), а
// `new Date()` такую строку читает как ЛОКАЛЬНОЕ время — и платёж, сделанный в
// 23:27 МСК, показывается как 20:27. Поймано владельцем на первом боевом
// платеже 20.08.2026. Разбор один на всех: третья копия форматирования дат в
// проекте и была той, где про `Z` забыли.
export function parseUtc(value) {
  if (!value) return null;
  const s = typeof value === 'string' && !value.endsWith('Z') && !value.includes('+')
    ? `${value}Z`
    : value;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Сдвиг даты на k календарных месяцев с прижатием к концу месяца: 31 января +
 * 1 мес = 28/29 февраля. Копия `add_months` из shared/billing_period.py — по ней
 * сервер и продлевает тариф, поэтому дата на кнопке обязана совпасть с его датой.
 */
export function addMonths(date, k) {
  const index = date.getUTCMonth() + k;
  const year = date.getUTCFullYear() + Math.floor(index / 12);
  const month = ((index % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const out = new Date(date.getTime());
  out.setUTCFullYear(year, month, Math.min(date.getUTCDate(), lastDay));
  return out;
}

/** Сколько дней до даты (вверх): сегодня — 0 или 1, уже прошла — отрицательное. Нет даты — null. */
export function daysUntil(value, now = new Date()) {
  const d = value instanceof Date ? value : parseUtc(value);
  return d ? Math.ceil((d.getTime() - now.getTime()) / 86400000) : null;
}

/** «сегодня», «завтра», «через 4 дн.» — для строки «тариф заканчивается …». */
export function endsInLabel(days) {
  if (days <= 0) return 'сегодня';
  return days === 1 ? 'завтра' : `через ${days} дн.`;
}

// За сколько дней до конца тарифа строка о сроке становится заметной.
export const RENEW_SOON_DAYS = 7;

/**
 * Что даст продление действующего тарифа: до какого числа оплачено сейчас и до
 * какого станет. Период прибавляется к дате окончания, а не к сегодняшнему дню —
 * оплаченные дни не сгорают (bit-billing, activate_subscription_period, ветка extend).
 */
export function renewalDates(paidUntil, period, now = new Date()) {
  const until = parseUtc(paidUntil);
  if (!until) return null;
  return { until, after: addMonths(until, period === 'year' ? 12 : 1), daysLeft: daysUntil(until, now) };
}

export function planHeadline(plan, period) {
  if (isPayg(plan)) {
    const rate = plan.overage_rub_per_minute;
    return typeof rate === 'number'
      ? { price: `${overageFormat.format(rate)} ₽`, unit: ' / мин', note: 'Без абонентской платы' }
      : { price: 'Без подписки', unit: '', note: null };
  }
  const price = planPrice(plan, period);
  return {
    price: price === 0 ? 'Бесплатно' : formatRub(price),
    unit: price === 0 ? '' : (period === 'month' ? ' / мес' : ' / год'),
    note: null,
  };
}

// Буллеты карточки тарифа. «Хранение 30 дн» и «Сверх пакета — N ₽/мин» убраны
// 24.09.2026 (проверка юртекстов по коду): срок хранения задаёт человек сам,
// тарифного ограничения нет; списания сверх пакета тоже нет — допуск 10 % и
// остановка (bit-metrics/app/billing_quota.py decide_action). Витрина обещала
// то, чего нет ни в продукте, ни в оферте.
export function planFeatures(plan) {
  if (isPayg(plan)) return paygFeatures(plan);
  const features = [
    `${numFormat.format(plan.minutes_per_period)} мин распознавания/мес`,
    `${numFormat.format(plan.llm_summaries_per_period)} LLM-протоколов/мес`,
  ];
  // Командная работа приезжает полем тарифа, а не зашита по коду плана: правило
  // «организация только на предоплаченном» живёт в одном месте, и его правка не
  // требует выката фронта.
  if (plan.team_access) features.push('Командная работа: общий пакет на сотрудников');
  return features;
}

function paygFeatures(plan) {
  const features = [`${numFormat.format(plan.minutes_per_period)} мин в месяц в подарок`];
  if (typeof plan.overage_rub_per_minute === 'number') {
    features.push(`Дальше — ${overageFormat.format(plan.overage_rub_per_minute)} ₽ за минуту с баланса`);
  }
  features.push('Баланс не сгорает, подписка не нужна');
  // Шаг правила приезжает из тарифа: своей копии числа у витрины быть не должно.
  const step = plan.summaries_per_paid_minutes;
  features.push(step
    ? `${numFormat.format(plan.llm_summaries_per_period)} LLM-протокола/мес + 1 за каждые ${numFormat.format(step)} оплаченных минут`
    : `${numFormat.format(plan.llm_summaries_per_period)} LLM-протокола/мес`);
  // Срока хранения у «Оплаты по факту» витрина не обещает (решение Владимира
  // 18.09.2026): хранится столько, сколько человек поставил в настройках, а
  // надпись «1 день» была строже, чем ведёт себя продукт.
  //
  // Отсутствие командной работы называется вслух: продать общий пакет можно
  // только там, где видно, что здесь его нет.
  if (!plan.team_access) features.push('Для одного человека — без общего пакета');
  return features;
}

// Запасная сетка для рендера при недоступном bit-billing (кнопки действий дизейблятся).
export const FALLBACK_PLANS = [
  {
    code: 'free', title: 'Оплата по факту',
    price_month_rub: 0, price_year_rub: 0,
    minutes_per_period: 100, llm_summaries_per_period: 3,
    overage_rub_per_minute: 1.00, summaries_per_paid_minutes: 30, team_access: false,
  },
  {
    code: 'small', title: 'Малый',
    price_month_rub: 4500, price_year_rub: 45000,
    team_access: true,
    minutes_per_period: 5000, llm_summaries_per_period: 100, history_retention_days: 30,
    overage_rub_per_minute: 0.90,
  },
  {
    code: 'medium', title: 'Средний',
    price_month_rub: 12000, price_year_rub: 120000,
    team_access: true,
    minutes_per_period: 15000, llm_summaries_per_period: 300, history_retention_days: 30,
    overage_rub_per_minute: 0.80,
  },
  {
    code: 'large', title: 'Большой',
    price_month_rub: 35000, price_year_rub: 350000,
    team_access: true,
    minutes_per_period: 50000, llm_summaries_per_period: 1000, history_retention_days: 30,
    overage_rub_per_minute: 0.70,
  },
];
